import {
  IAccountRateCreateReqSchemaUI,
  IAccountRateUpdateReqSchemaUI,
  IRateResSchema,
  RateTypeEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { Controller, useFormContext } from 'react-hook-form';
import { MdClear, MdMoreVert } from 'react-icons/md';
import { QuoteOfferingRateEditability } from '../../../api/quoteOfferingRateService';
import { RATE_BILLING_FREQUENCY_MAP } from '../../../constants/offerings';
import { useExperienceConfiguration } from '../../../hooks';
import { useAuth } from '../../../services/auth0';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MDropdownActionItem,
  MDropdownActions,
  MFlex,
  MFormField,
  MInput,
  MText,
  MTextarea,
  MTooltip,
} from '../../Monetize';

const CLONE_HELP_TEXT =
  'This rate cannot be edited but can be copied to create a new account-specific rate.';
const EDIT_SHARED_HELP_TEXT =
  'Changes will affect other quote offerings on this quote using this rate.';

interface PricesDrawerRateHeaderProps {
  offeringRate: IRateResSchema;
  isEditMode: boolean;
  disabled?: boolean;
  showPriceTierLock?: boolean;
  isPriceTierLockingMode?: boolean;
  editability?: QuoteOfferingRateEditability;
  isEditabilityLoading?: boolean;
  onClone: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onManagePriceTierLocking?: () => void;
}

export const PricesDrawerRateHeader = ({
  offeringRate,
  isEditMode,
  disabled,
  showPriceTierLock,
  isPriceTierLockingMode,
  editability,
  isEditabilityLoading,
  onClone,
  onEdit,
  onCancelEdit,
  onManagePriceTierLocking,
}: PricesDrawerRateHeaderProps) => {
  const {
    control,
    reset,
    formState: { errors },
  } = useFormContext<
    IAccountRateCreateReqSchemaUI | IAccountRateUpdateReqSchemaUI
  >();
  const isCustomRate = offeringRate?.rateType === RateTypeEnum.ACCOUNT;
  const { currentTenantUserHasRole } = useAuth();
  const {
    accountSpecificRateCreation: accountSpecificRateCreationRoles,
    enablePriceTierLockEligible,
  } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );

  const canEditRate = currentTenantUserHasRole(
    accountSpecificRateCreationRoles ?? [],
  );

  const showEditButton = !!editability?.editable;
  const showTierLock = !!enablePriceTierLockEligible && !!showPriceTierLock;
  const hasOverflowActions = showEditButton || showTierLock;

  return (
    <MFlex justify="space-between" gap="2" pb="2" align="flex-start">
      <MBox flex="1">
        {isEditMode ? (
          <MFormField
            isHorizontal
            labelProps={{ fontSize: 'md' }}
            label="Rate Name"
            error={errors.name}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        ) : (
          <MFlex h="8" align="center">
            <MText as="h5" fontSize="md" fontWeight="bold" lineClamp={1}>
              Rate Name{' '}
              <MText as="span" fontWeight="normal">
                {offeringRate.name}
              </MText>
            </MText>
          </MFlex>
        )}

        {isEditMode ? (
          <MFormField
            mt={2}
            labelProps={{ fontSize: 'md' }}
            label="Internal Description"
            error={errors.internalDescription}
            tooltip="Optional description for internal reference, this will not be visible to customers."
          >
            <Controller
              name="internalDescription"
              control={control}
              render={({ field }) => (
                <MTextarea {...field} rows={1} value={field.value || ''} />
              )}
            />
          </MFormField>
        ) : (
          offeringRate.internalDescription && (
            <MFlex h="8" align="center">
              <MText as="h5" fontSize="md" fontWeight="bold" lineClamp={1}>
                Internal Description{' '}
                <MText as="span" fontWeight="normal">
                  {offeringRate.internalDescription}
                </MText>
              </MText>
            </MFlex>
          )
        )}

        <MText mt="2" as="h5" fontSize="md" fontWeight="bold" lineClamp={1}>
          Subscription Frequency{' '}
          <MText as="span" fontWeight="normal">
            {offeringRate.billingFrequency
              ? RATE_BILLING_FREQUENCY_MAP[offeringRate.billingFrequency](
                  offeringRate.billingFrequencyInMonths,
                ).label
              : ''}
          </MText>
        </MText>

        {isCustomRate && (
          <MText mt="2" fontSize="sm" fontStyle="italic" lineClamp={1}>
            This rate has been customized for this account.
          </MText>
        )}
      </MBox>
      {!isPriceTierLockingMode && (
        <MBox>
          {isEditMode ? (
            <MCustomIconButton
              justifySelf="center"
              variant="icon"
              icon={MdClear}
              aria-label="Cancel edit"
              mr={-2}
              containerSize={8}
              onClick={() => {
                reset();
                onCancelEdit();
              }}
            />
          ) : (
            canEditRate &&
            (isEditabilityLoading ? (
              <MFlex align="center" justify="flex-end" gap="1">
                <MButton
                  variant="tertiary"
                  type="button"
                  minW={16}
                  fontWeight="normal"
                  isLoading
                  isDisabled
                >
                  Copy and Edit
                </MButton>
              </MFlex>
            ) : (
              <MFlex align="center" justify="flex-end" gap="1">
                {showEditButton ? (
                  <MTooltip
                    label={
                      editability?.usedByOtherOfferingsOnThisQuote
                        ? EDIT_SHARED_HELP_TEXT
                        : undefined
                    }
                    placement="bottom-end"
                    hasArrow
                  >
                    <MButton
                      isDisabled={disabled}
                      variant="tertiary"
                      type="button"
                      minW={16}
                      fontWeight="normal"
                      onClick={onEdit}
                    >
                      Edit
                    </MButton>
                  </MTooltip>
                ) : (
                  <MTooltip
                    label={editability ? CLONE_HELP_TEXT : undefined}
                    placement="bottom-end"
                    hasArrow
                  >
                    <MButton
                      isDisabled={disabled}
                      variant="tertiary"
                      type="button"
                      minW={16}
                      fontWeight="normal"
                      onClick={onClone}
                    >
                      Copy and Edit
                    </MButton>
                  </MTooltip>
                )}

                {hasOverflowActions && (
                  <MDropdownActions
                    isDisabled={disabled}
                    menuButtonProps={{ asChild: true }}
                    renderTrigger={() => (
                      <MCustomIconButton
                        variant="icon"
                        icon={MdMoreVert}
                        aria-label="More rate actions"
                        containerSize={8}
                        isDisabled={disabled}
                      />
                    )}
                  >
                    {showEditButton && (
                      <MDropdownActionItem onClick={onClone}>
                        <MText color="inherit">Copy and Edit</MText>
                      </MDropdownActionItem>
                    )}
                    {showTierLock && (
                      <MDropdownActionItem
                        onClick={() => onManagePriceTierLocking?.()}
                      >
                        <MText color="inherit">Manage Tier Lock</MText>
                      </MDropdownActionItem>
                    )}
                  </MDropdownActions>
                )}
              </MFlex>
            ))
          )}
        </MBox>
      )}
    </MFlex>
  );
};
